import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { moldService } from '../services/mold.service';
import { lifeCycleService } from '../services/lifeCycle.service';
import { notificationService } from '../services/notification.service';
import { useAuth } from '../context/AuthContext';
import { Mold } from '../types';
import LifeCycleIndicator from '../components/LifeCycleIndicator';

const Glyph = ({ icon, size = '1.2rem' }: { icon: string; size?: string }) => (
  <Box
    component="span"
    className="material-symbols-rounded"
    sx={{
      fontSize: size,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
    }}
  >
    {icon}
  </Box>
);

const formatNumber = (value: number) => value.toLocaleString('ko-KR');

const Dashboard2: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { authUser, user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [molds, setMolds] = useState<Mold[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    inUse: 0,
    stored: 0,
    needCheck: 0,
  });
  const [alertChecked, setAlertChecked] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, [user]);

  useEffect(() => {
    if (authUser && !alertChecked) {
      console.log('🔔 수명 알림 체크 시작...');
      notificationService.checkAndSendLifetimeAlerts(authUser.uid)
        .then(() => {
          console.log('✅ 수명 알림 체크 완료');
          setAlertChecked(true);
        })
        .catch(err => {
          console.error('❌ 알림 체크 실패:', err);
        });
    }
  }, [authUser, alertChecked]);

  const loadDashboardData = async () => {
    try {
      let moldList: Mold[];
      if (user && (user.role === 'customer' || user.role === 'partner') && user.customerId) {
        moldList = await moldService.getMoldsByCustomerId(user.customerId);
      } else {
        moldList = await moldService.getMolds();
      }
      
      setMolds(moldList);

      const inUse = moldList.filter((mold) => mold.status === 'in-use').length;
      const stored = moldList.filter((mold) => mold.status === 'stored').length;
      const needCheck = moldList.filter((mold) => lifeCycleService.shouldTriggerAlert(mold)).length;

      setStats({
        total: moldList.length,
        inUse,
        stored,
        needCheck,
      });
    } catch (error) {
      console.error('대시보드 데이터를 불러오는 중 오류가 발생했습니다:', error);
    }
  };

  const alertMolds = useMemo(
    () =>
      molds
        .filter((mold) => lifeCycleService.shouldTriggerAlert(mold))
        .slice(0, 6),
    [molds],
  );

  const handleMoldClick = (moldId: string) => {
    navigate(`/molds/${moldId}`);
  };

  const handleCardClick = (filter: 'all' | 'in-use' | 'stored' | 'needCheck') => {
    navigate('/molds', { state: { filter } });
  };

  const compactCards = useMemo(() => {
    const inUseRatio = stats.total ? Math.round((stats.inUse / stats.total) * 100) : 0;
    const storedRatio = stats.total ? Math.round((stats.stored / stats.total) * 100) : 0;
    const needCheckRatio = stats.total ? Math.round((stats.needCheck / stats.total) * 100) : 0;

    return [
      {
        key: 'total',
        label: '총 금형',
        icon: 'inventory_2',
        value: stats.total,
        color: theme.palette.primary.main,
        bgColor: alpha(theme.palette.primary.main, 0.08),
        filter: 'all' as const,
      },
      {
        key: 'inUse',
        label: '사용중',
        icon: 'bolt',
        value: stats.inUse,
        sub: `${inUseRatio}%`,
        color: '#fdd835',
        bgColor: alpha('#fdd835', 0.08),
        filter: 'in-use' as const,
      },
      {
        key: 'stored',
        label: '보관중',
        icon: 'shelves',
        value: stats.stored,
        sub: `${storedRatio}%`,
        color: '#4caf50',
        bgColor: alpha('#4caf50', 0.08),
        filter: 'stored' as const,
      },
      {
        key: 'needCheck',
        label: '점검 필요',
        icon: 'warning',
        value: stats.needCheck,
        sub: `${needCheckRatio}%`,
        color: '#f44336',
        bgColor: alpha('#f44336', 0.08),
        filter: 'needCheck' as const,
      },
    ];
  }, [stats, theme]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700 }}>
            대시보드
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            생산 현황과 리스크 지표를 한눈에 확인해 보세요.
          </Typography>
        </Box>
        <Chip
          icon={<Glyph icon="auto_graph" size="1rem" />}
          label="실시간"
          color="secondary"
          size="small"
        />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 2,
        }}
      >
        {compactCards.map((card) => (
          <Card
            key={card.key}
            elevation={0}
            sx={{
              border: `1px solid ${alpha(card.color, 0.2)}`,
              backgroundColor: card.bgColor,
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 6px 16px ${alpha(card.color, 0.25)}`,
                backgroundColor: alpha(card.color, 0.12),
              },
            }}
            onClick={() => handleCardClick(card.filter)}
          >
            <CardContent sx={{ p: 2 }}>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Glyph icon={card.icon} size="1.5rem" />
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: card.color }}
                  >
                    {formatNumber(card.value)}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {card.label}
                  </Typography>
                  {card.sub && (
                    <Typography variant="caption" sx={{ color: card.color, fontWeight: 600 }}>
                      {card.sub}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>

      {alertMolds.length > 0 && (
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
            backgroundColor: alpha(theme.palette.secondary.main, 0.05),
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                점검 시급 금형
              </Typography>
              <Chip
                icon={<Glyph icon="priority_high" size="1rem" />}
                label={`${alertMolds.length}건`}
                color="warning"
                size="small"
              />
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(3, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(6, 1fr)',
                },
                gap: 1.5,
              }}
            >
              {alertMolds.map((mold) => (
                <Card
                  key={mold.id}
                  elevation={0}
                  sx={{
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                    backgroundColor: alpha(theme.palette.warning.main, 0.08),
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.25)}`,
                      backgroundColor: alpha(theme.palette.warning.main, 0.12),
                    },
                  }}
                  onClick={() => mold.id && handleMoldClick(mold.id)}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack spacing={1}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Glyph icon="warning" size="1.2rem" />
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem' }}>
                          {formatNumber(mold.designLife)}회
                        </Typography>
                      </Stack>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {mold.moldNumber}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {mold.moldName}
                      </Typography>
                      <LifeCycleIndicator
                        currentShots={mold.currentShots}
                        designLife={mold.designLife}
                        size="small"
                        showDetails={false}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {stats.total === 0 && (
        <Alert severity="info">
          등록된 금형이 없습니다. 금형을 등록해 주세요.
        </Alert>
      )}
    </Box>
  );
};

export default Dashboard2;
